import toast from 'react-hot-toast'
import { 
  CheckCircle, 
  AlertTriangle, 
  AlertCircle, 
  Info, 
  X,
  Circle
} from 'lucide-react'

// Default toast (cyan theme)
export const showDefaultToast = (message: string, actions?: { label: string; onClick: () => void }[]) => {
  toast((t) => (
    <div className="flex items-center space-x-3 bg-cyan-240 rounded-lg px-4 py-3 min-w-[725px] min-h-[48px]">
      <Info className="w-5 h-5 text-black flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm text-black whitespace-nowrap">{message}</p>
      </div>
      {actions && (
        <div className="flex space-x-4">
          {actions.map((action, index) => (
            <button
              key={index}
              onClick={() => {
                action.onClick()
                toast.dismiss(t.id)
              }}
              className="text-sm text-black hover:text-gray-700 font-medium whitespace-nowrap"
            >
              {action.label}
            </button>
          ))}
        </div>
      )}
      <button 
        onClick={() => toast.dismiss(t.id)}
        className="text-black hover:text-gray-700 flex-shrink-0"
      >
        <X className="w-5 h-5" />
      </button>
    </div>
  ), { 
    duration: 4000,
    id: 'default-toast',
    position: 'bottom-center',
    className: 'toast-wide-centered',
    style: {
      background: 'transparent',
      boxShadow: 'none',
      padding: '0',
      margin: '0'
    }
  })
}

// Success toast (green theme)
export const showSuccessToast = (message: string, actions?: { label: string; onClick: () => void }[]) => {
  toast((t) => (
    <div className="flex items-center space-x-3 bg-green-400 rounded-lg px-4 py-3 min-w-[725px] h-[48px]">
      <CheckCircle className="w-5 h-5 text-black flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm text-black whitespace-nowrap">{message}</p>
      </div>
      {actions && (
        <div className="flex space-x-4">
          {actions.map((action, index) => (
            <button
              key={index}
              onClick={() => {
                action.onClick()
                toast.dismiss(t.id)
              }}
              className="text-sm text-black hover:text-gray-700 font-medium whitespace-nowrap"
            >
              {action.label}
            </button>
          ))}
        </div>
      )}
      <button 
        onClick={() => toast.dismiss(t.id)}
        className="text-black hover:text-gray-700 flex-shrink-0"
      >
        <X className="w-5 h-5" />
      </button>
    </div>
  ), { 
    duration: 3000,
    id: 'success-toast',
    position: 'bottom-center',
    className: 'toast-wide-centered',
    style: {
      background: 'transparent',
      boxShadow: 'none',
      padding: '0',
      margin: '0'
    }
  })
}

// Warning toast (yellow theme)
export const showWarningToast = (message: string, actions?: { label: string; onClick: () => void }[]) => {
  toast((t) => (
    <div className="flex items-center space-x-3 bg-yellow-450 rounded-lg px-4 py-3 min-w-[725px] h-[48px]">
      <AlertTriangle className="w-5 h-5 text-black flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm text-black whitespace-nowrap">{message}</p>
      </div>
      {actions && (
        <div className="flex space-x-4">
          {actions.map((action, index) => (
            <button
              key={index}
              onClick={() => {
                action.onClick()
                toast.dismiss(t.id)
              }}
              className="text-sm text-black hover:text-gray-700 font-medium whitespace-nowrap"
            >
              {action.label}
            </button>
          ))}
        </div>
      )}
      <button 
        onClick={() => toast.dismiss(t.id)}
        className="text-black hover:text-gray-700 flex-shrink-0"
      >
        <X className="w-5 h-5" />
      </button>
    </div>
  ), { 
    duration: 5000,
    id: 'warning-toast',
    position: 'bottom-center',
    className: 'toast-wide-centered',
    style: {
      background: 'transparent',
      boxShadow: 'none',
      padding: '0',
      margin: '0'
    }
  })
}

// Destructive/Error toast (red theme)
export const showErrorToast = (message: string, actions?: { label: string; onClick: () => void }[]) => {
  toast((t) => (
    <div className="flex items-center space-x-3 bg-red-550 rounded-lg px-4 py-3 min-w-[725px] min-h-[48px]">
      <AlertCircle className="w-5 h-5 text-white flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm text-white whitespace-nowrap">{message}</p>
      </div>
      {actions && (
        <div className="flex space-x-4">
          {actions.map((action, index) => (
            <button
              key={index}
              onClick={() => {
                action.onClick()
                toast.dismiss(t.id)
              }}
              className="text-sm text-white hover:text-gray-200 font-medium whitespace-nowrap"
            >
              {action.label}
            </button>
          ))}
        </div>
      )}
      <button 
        onClick={() => toast.dismiss(t.id)}
        className="text-white hover:text-gray-200 flex-shrink-0"
      >
        <X className="w-5 h-5" />
      </button>
    </div>
  ), { 
    duration: 5000,
    id: 'error-toast',
    position: 'bottom-center',
    className: 'toast-wide-centered',
    style: {
      background: 'transparent',
      boxShadow: 'none',
      padding: '0',
      margin: '0'
    }
  })
}

// Branding toast (orange theme)
export const showBrandingToast = (message: string, actions?: { label: string; onClick: () => void }[]) => {
  toast((t) => (
    <div className="flex items-center space-x-3 bg-orange-500 rounded-lg px-4 py-3 min-w-[725px] h-[48px]">
      <Info className="w-5 h-5 text-white flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm text-white whitespace-nowrap">{message}</p>
      </div>
      {actions && (
        <div className="flex space-x-4">
          {actions.map((action, index) => (
            <button
              key={index}
              onClick={() => {
                action.onClick()
                toast.dismiss(t.id)
              }}
              className="text-sm text-white hover:text-gray-200 font-medium whitespace-nowrap"
            >
              {action.label}
            </button>
          ))}
        </div>
      )}
      <button 
        onClick={() => toast.dismiss(t.id)}
        className="text-white hover:text-gray-200 flex-shrink-0"
      >
        <X className="w-5 h-5" />
      </button>
    </div>
  ), { 
    duration: 4000,
    id: 'branding-toast',
    position: 'bottom-center',
    className: 'toast-wide-centered',
    style: {
      background: 'transparent',
      boxShadow: 'none',
      padding: '0',
      margin: '0'
    }
  })
}

// Convenience function for backwards compatibility
export const showToast = {
  default: showDefaultToast,
  success: showSuccessToast,
  warning: showWarningToast,
  error: showErrorToast,
  branding: showBrandingToast,
} 